import { AudienceFilterOperator } from './components/audience.model';
import { SessionCondition, SessionConditionType } from './components/session-condition-group.model';

/** An editor choice, stored as an operator plus the condition's `show` flag. */
export enum SessionConditionComparison {
  Is = 'Is',
  IsNot = 'IsNot',
  Equals = 'Equals',
  DoesNotEqual = 'DoesNotEqual',
  Contains = 'Contains',
  DoesNotContain = 'DoesNotContain',
  StartsWith = 'StartsWith',
  DoesNotStartWith = 'DoesNotStartWith',
  EndsWith = 'EndsWith',
  DoesNotEndWith = 'DoesNotEndWith',
  MoreThan = 'MoreThan',
  LessThan = 'LessThan',
}

export interface SessionConditionComparisonOption {
  value: SessionConditionComparison;
  labelKey: string;
}

/** Less than N matches visits 2 to N-1, so 3 is the lowest value that matches anyone. */
export const RETURNING_VISITOR_LESS_THAN_MINIMUM = 3;

interface ComparisonSpec {
  /** Undefined keeps the condition's own operator. */
  operator?: AudienceFilterOperator;
  show: boolean;
  labelKey: string;
}

const COMPARISON_SPECS: Readonly<Record<SessionConditionComparison, ComparisonSpec>> = {
  [SessionConditionComparison.Is]: { show: true, labelKey: 'audience.operators.Is' },
  [SessionConditionComparison.IsNot]: { show: false, labelKey: 'audience.operators.IsNot' },
  [SessionConditionComparison.Equals]: {
    operator: AudienceFilterOperator.Equals,
    show: true,
    labelKey: 'audience.operators.Equals',
  },
  [SessionConditionComparison.DoesNotEqual]: {
    operator: AudienceFilterOperator.Equals,
    show: false,
    labelKey: 'audience.operators.DoesNotEqual',
  },
  [SessionConditionComparison.Contains]: {
    operator: AudienceFilterOperator.Contains,
    show: true,
    labelKey: 'audience.operators.Contains',
  },
  [SessionConditionComparison.DoesNotContain]: {
    operator: AudienceFilterOperator.Contains,
    show: false,
    labelKey: 'audience.operators.DoesNotContain',
  },
  [SessionConditionComparison.StartsWith]: {
    operator: AudienceFilterOperator.StartsWith,
    show: true,
    labelKey: 'audience.operators.StartsWith',
  },
  [SessionConditionComparison.DoesNotStartWith]: {
    operator: AudienceFilterOperator.StartsWith,
    show: false,
    labelKey: 'audience.operators.DoesNotStartWith',
  },
  [SessionConditionComparison.EndsWith]: {
    operator: AudienceFilterOperator.EndsWith,
    show: true,
    labelKey: 'audience.operators.EndsWith',
  },
  [SessionConditionComparison.DoesNotEndWith]: {
    operator: AudienceFilterOperator.EndsWith,
    show: false,
    labelKey: 'audience.operators.DoesNotEndWith',
  },
  [SessionConditionComparison.MoreThan]: {
    operator: AudienceFilterOperator.MoreThan,
    show: true,
    labelKey: 'audience.session-condition-groups.campaignEventCountOperators.MoreThan',
  },
  [SessionConditionComparison.LessThan]: {
    operator: AudienceFilterOperator.LessThan,
    show: true,
    labelKey: 'audience.session-condition-groups.campaignEventCountOperators.LessThan',
  },
};

const VALUELESS_COMPARISONS: readonly SessionConditionComparison[] = [
  SessionConditionComparison.Is,
  SessionConditionComparison.IsNot,
];

const TEXT_COMPARISONS: readonly SessionConditionComparison[] = [
  SessionConditionComparison.Equals,
  SessionConditionComparison.DoesNotEqual,
  SessionConditionComparison.Contains,
  SessionConditionComparison.DoesNotContain,
  SessionConditionComparison.StartsWith,
  SessionConditionComparison.DoesNotStartWith,
  SessionConditionComparison.EndsWith,
  SessionConditionComparison.DoesNotEndWith,
];

const VISIT_COUNT_COMPARISONS: readonly SessionConditionComparison[] = [
  SessionConditionComparison.MoreThan,
  SessionConditionComparison.LessThan,
];

const VALUELESS_TYPES: ReadonlySet<SessionConditionType> = new Set([
  SessionConditionType.OnlyMobile,
  SessionConditionType.OnlyDesktop,
  SessionConditionType.Tablet,
  SessionConditionType.MobileOrTablet,
  SessionConditionType.NewVisitor,
  SessionConditionType.FacebookVisit,
  SessionConditionType.InstagramVisit,
  SessionConditionType.OtherSocialMediaVisit,
  SessionConditionType.MarketingEmailVisit,
]);

const TEXT_TYPES: ReadonlySet<SessionConditionType> = new Set([
  SessionConditionType.QueryParam,
  SessionConditionType.UTMCampaign,
  SessionConditionType.UTMSource,
  SessionConditionType.UTMMedium,
]);

/** Saved rows may carry a negating operator; `show` false on top of one reads as the positive. */
const POSITIVE_OF_NEGATED_OPERATOR: Partial<Record<AudienceFilterOperator, AudienceFilterOperator>> = {
  [AudienceFilterOperator.NotEquals]: AudienceFilterOperator.Equals,
  [AudienceFilterOperator.NotContains]: AudienceFilterOperator.Contains,
};

/** Undefined when the type keeps its own operators. */
export function comparisonsFor(
  type: SessionConditionType | undefined,
): readonly SessionConditionComparison[] | undefined {
  if (!type) {
    return undefined;
  }
  if (VALUELESS_TYPES.has(type)) {
    return VALUELESS_COMPARISONS;
  }
  if (TEXT_TYPES.has(type)) {
    return TEXT_COMPARISONS;
  }
  if (type === SessionConditionType.ReturningVisitor) {
    return VISIT_COUNT_COMPARISONS;
  }
  return undefined;
}

export function comparisonOptionsFor(type: SessionConditionType | undefined): SessionConditionComparisonOption[] {
  return (comparisonsFor(type) ?? []).map((value) => ({ value, labelKey: comparisonLabelKey(value) }));
}

export function toComparison(condition: SessionCondition): SessionConditionComparison | undefined {
  const type = condition.type;
  if (!type) {
    return undefined;
  }
  if (VALUELESS_TYPES.has(type)) {
    return condition.show === false ? SessionConditionComparison.IsNot : SessionConditionComparison.Is;
  }
  if (TEXT_TYPES.has(type)) {
    return textComparison(condition);
  }
  if (type === SessionConditionType.ReturningVisitor) {
    // The widget script reads every other operator as more than.
    return condition.operator === AudienceFilterOperator.LessThan
      ? SessionConditionComparison.LessThan
      : SessionConditionComparison.MoreThan;
  }
  return undefined;
}

/** An unchanged comparison keeps the stored operator and show, so a re-save never changes what a row matches. */
export function applyComparison(condition: SessionCondition, comparison: SessionConditionComparison): SessionCondition {
  if (toComparison(condition) === comparison) {
    return { ...condition };
  }
  const spec = COMPARISON_SPECS[comparison];
  return { ...condition, operator: spec.operator ?? condition.operator, show: spec.show };
}

export function comparisonOperator(comparison: SessionConditionComparison): AudienceFilterOperator | undefined {
  return COMPARISON_SPECS[comparison].operator;
}

export function isValuelessType(type: SessionConditionType | undefined): boolean {
  return !!type && VALUELESS_TYPES.has(type);
}

export function comparisonLabelKey(comparison: SessionConditionComparison): string {
  return COMPARISON_SPECS[comparison].labelKey;
}

/** Only Returning visitor offers less than. */
export function minimumValueFor(comparison: SessionConditionComparison | undefined): number | undefined {
  return comparison === SessionConditionComparison.LessThan ? RETURNING_VISITOR_LESS_THAN_MINIMUM : undefined;
}

export function isBelowMinimum(condition: SessionCondition): boolean {
  const minimum = minimumValueFor(toComparison(condition));
  return minimum !== undefined && !(Number(condition.value) >= minimum);
}

/** One type as both is and is not, or Only mobile and Only desktop with the same choice (every visitor is on one). */
export function hasConditionsThatNeverMatch(conditions: readonly SessionCondition[]): boolean {
  const choices = conditions
    .filter((condition) => isValuelessType(condition.type))
    .map((condition) => ({ type: condition.type, negated: condition.show === false }));
  return choices.some((first) =>
    choices.some(
      (second) =>
        (first.type === second.type && first.negated !== second.negated) ||
        (first.type === SessionConditionType.OnlyMobile &&
          second.type === SessionConditionType.OnlyDesktop &&
          first.negated === second.negated),
    ),
  );
}

function textComparison(condition: SessionCondition): SessionConditionComparison {
  const operator = condition.operator ?? AudienceFilterOperator.Equals;
  const positive = POSITIVE_OF_NEGATED_OPERATOR[operator] ?? operator;
  const negated = (positive !== operator) !== (condition.show === false);
  return (
    TEXT_COMPARISONS.find(
      (candidate) => COMPARISON_SPECS[candidate].operator === positive && COMPARISON_SPECS[candidate].show !== negated,
    ) ?? (negated ? SessionConditionComparison.DoesNotEqual : SessionConditionComparison.Equals)
  );
}
